import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ArrowLeft, BookOpen, Printer, MessageSquare, Save } from 'lucide-react';
import { toast } from 'sonner';
import AIPracticeScenario from '../shared/AIPracticeScenario';
import PageMessaging from '../shared/PageMessaging';

export default function IntimacyQuestions() {
  const navigate = useNavigate();
  const printPage = () => { window.print(); toast.success('Print dialog opened'); };
  
  const [journalEntry, setJournalEntry] = useState({ question: '', response: '' });

  const saveToJournal = async () => {
    const userId = localStorage.getItem('workbook_user_id');
    if (!userId) {
      toast.error('Please log in to save to journal');
      return;
    }

    if (!journalEntry.question || !journalEntry.response) {
      toast.error('Please select a question and write a response');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workbook/journal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          title: `Intimacy Question: ${journalEntry.question.substring(0, 50)}...`,
          content: `Question: ${journalEntry.question}\n\nResponse: ${journalEntry.response}`,
          tags: ['intimacy', 'page-46']
        })
      });

      if (response.ok) {
        toast.success('Saved to your journal!');
        setJournalEntry({ question: '', response: '' });
      } else {
        toast.error('Failed to save to journal');
      }
    } catch (error) {
      toast.error('Error saving to journal');
    }
  };

  const questionCategories = [
    {
      title: 'Values & Life',
      questions: [
        'What\'s something you really value and appreciate about yourself?',
        'What makes you feel alive?',
        'What do you really love about your life right now?',
        'What\'s something missing for you in your life?',
        'What\'s the most challenging thing you\'re dealing with right now?',
        'What\'s something about yourself you don\'t normally tell people you just met?'
      ]
    },
    {
      title: 'Childhood & Growth',
      questions: [
        'When you were an adolescent, what was one thing you really liked?',
        'When you were 10-11, what did you want to be when you grew up?',
        'If you could change one thing about your childhood, what would it be?',
        'What is one of the things you most loved about your father?',
        'What is one of the things you most loved about your mother?',
        'What is something you wish your father had said to you?',
        'What is something you wish your mother had said to you?'
      ]
    },
    {
      title: 'Relationships & Connection',
      questions: [
        'What do you value most in a friendship?',
        'Who in your life are you most grateful for?',
        'Is there someone you are holding a grudge against? Who? For what?',
        'How many siblings do you have? Describe your relationships with them.',
        'Describe one of your most pleasant memories with a sibling or friend.'
      ]
    },
    {
      title: 'Pride & Regret',
      questions: [
        'What is one of the happiest memories of your life?',
        'What is one of the proudest achievements of your life?',
        'What is one of the biggest disappointments of your life?',
        'What is something you did as a child that you now regret?',
        'What is something you did as an adult that you now regret?',
        'What mistake have you learned the most from?'
      ]
    },
    {
      title: 'Dreams & Future',
      questions: [
        'Have you ever had a big dream that changed you? What was it?',
        'If you woke up having one new quality or ability, what would you want it be?',
        'If you only had a year to live, what is one thing you would be sure to do?',
        'If you died today, what would you most regret not having said or done?',
        'What new habit could you develop that your future self would thank you for?'
      ]
    },
    {
      title: 'Self-Awareness',
      questions: [
        'What is something most people don\'t know about you?',
        'What is something you\'re really good at?',
        'What is something about your body that you really like?',
        'What is something about your body that you like the least?',
        'When do you feel most alive?',
        'What\'s one thing that really helps you when you\'re feeling down?'
      ]
    },
    {
      title: 'Relational Questions (In-Person)',
      questions: [
        'Name someone in the room who you are attracted to physically or otherwise.',
        'Name someone in the room you are curious about.',
        'Name someone in the room who reminds you of someone else in your life.',
        'What do you most want to get out of this class?',
        'What are three things that you think we have in common?',
        'What\'s something you really want me to know about you? Why?'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
              <div><h1 className="text-2xl font-bold text-white">Intimacy Building Questions</h1></div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white"><Printer className="h-4 w-4 mr-2" />Print</Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm"><BookOpen className="h-4 w-4" /><span>Pages 46-47</span></div>
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8"><MessageSquare className="h-16 w-16 text-rose-400 mx-auto mb-4" /><h2 className="text-3xl font-bold text-white mb-4">Intimacy Building Questions</h2><p className="text-slate-300">Deep conversation starters for meaningful connection</p></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {questionCategories.map((category, catIdx) => (
              <div key={catIdx} className="bg-slate-800/40 border border-slate-700/50 rounded-lg overflow-hidden">
                <div className="bg-slate-800/60 px-5 py-3 border-b border-slate-700/30">
                  <h3 className="text-lg font-bold text-white">{category.title}</h3>
                </div>
                <div className="p-5">
                  <ul className="space-y-3">
                    {category.questions.map((q, qIdx) => (
                      <li key={qIdx} className="text-slate-200 text-sm leading-relaxed flex items-start gap-2">
                        <button 
                          onClick={() => setJournalEntry({...journalEntry, question: q})}
                          className="text-purple-400 mt-0.5 hover:text-purple-300 cursor-pointer"
                        >
                          •
                        </button>
                        <span>{q}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Journal Entry Section */}
          <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-6 mb-8">
            <h3 className="text-xl font-bold text-green-200 mb-4">📝 Save Question & Response to Your Journal</h3>
            <div className="space-y-4">
              <div>
                <label className="text-green-200 text-sm mb-2 block">Selected Question:</label>
                <Textarea 
                  value={journalEntry.question}
                  onChange={(e) => setJournalEntry({...journalEntry, question: e.target.value})}
                  placeholder="Click a bullet point above to select a question, or type your own..."
                  className="bg-green-900/20 border-green-700/30 text-white placeholder:text-green-300/40 min-h-[60px]"
                />
              </div>
              <div>
                <label className="text-green-200 text-sm mb-2 block">Your Response:</label>
                <Textarea 
                  value={journalEntry.response}
                  onChange={(e) => setJournalEntry({...journalEntry, response: e.target.value})}
                  placeholder="Reflect on your answer here..."
                  className="bg-green-900/20 border-green-700/30 text-white placeholder:text-green-300/40 min-h-[120px]"
                />
              </div>
              <Button onClick={saveToJournal} className="bg-green-600 hover:bg-green-500 text-white w-full">
                <Save className="h-4 w-4 mr-2" />
                Save to My Journal
              </Button>
            </div>
          </div>

          <AIPracticeScenario pageTitle="Intimacy Building Questions" pageNumber={46} />
          <PageMessaging pageTitle="Intimacy Building Questions" pageNumber={46} />
        </div>
      </div>
    </div>
  );
}
